import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PaymentSuccessPage, PaymentCancelledPage } from '../components/MyPOSPayment';
import { stripeAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FiCheckCircle, FiXCircle, FiLoader, FiAlertCircle } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');
  const reservationId = searchParams.get('reservation_id');
  
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    // Stripe payment
    if (sessionId) {
      pollStripeStatus(sessionId);
    }
    // MyPOS payment
    else if (orderId) {
      fetch(`${API_URL}/api/payments/mypos/status/${orderId}`)
        .then(res => res.json())
        .then(data => {
          setPaymentStatus(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching payment status:', err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [sessionId, orderId]);

  const pollStripeStatus = async (sid, attempts = 0) => {
    if (attempts >= maxAttempts) {
      setLoading(false);
      setPaymentStatus({ status: 'pending', message: 'Vérification en cours. Vous recevrez une confirmation par email.' });
      return;
    }

    try {
      const response = await stripeAPI.getPaymentStatus(sid);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setPaymentStatus({ status: 'paid', ...data });
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Session expirée' });
        setLoading(false);
        return;
      }
      
      // Continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollStripeStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Erreur lors de la vérification du paiement');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FiLoader className="w-16 h-16 text-indigo-600 mx-auto animate-spin" />
            <h2 className="text-2xl font-bold text-slate-900 mt-4">Vérification du paiement...</h2>
            <p className="text-slate-600 mt-2">Veuillez patienter pendant que nous confirmons votre paiement.</p>
            {pollAttempts > 0 && (
              <p className="text-sm text-slate-500 mt-2">Tentative {pollAttempts}/{maxAttempts}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FiAlertCircle className="w-16 h-16 text-amber-500 mx-auto" />
            <h2 className="text-2xl font-bold text-slate-900 mt-4">Vérification impossible</h2>
            <p className="text-slate-600 mt-2">{error}</p>
            <p className="text-sm text-slate-500 mt-2">Vous recevrez une confirmation par email si le paiement a été effectué.</p>
            <Button onClick={() => navigate('/')} className="mt-6">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If Stripe payment was successful
  if (sessionId && paymentStatus?.status === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <FiCheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mt-6">Paiement réussi !</h2>
            <p className="text-slate-600 mt-2">
              Votre paiement de <strong>{paymentStatus.amount?.toFixed(2)} €</strong> a été confirmé.
            </p>
            {reservationId && (
              <p className="text-sm text-slate-500 mt-2">
                Réservation #{reservationId.slice(0, 8)}
              </p>
            )}
            <p className="text-sm text-slate-500 mt-4">
              Un email de confirmation vous sera envoyé avec tous les détails.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <Button onClick={() => navigate('/')} className="w-full">
                Retour à l'accueil
              </Button>
              {reservationId && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/client-portal?reservation=${reservationId}`)}
                  className="w-full"
                >
                  Voir ma réservation
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fall back to MyPOS success page for MyPOS payments
  return (
    <PaymentSuccessPage 
      orderId={orderId}
      onContinue={() => navigate('/')}
    />
  );
};

export const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  const reservationId = searchParams.get('reservation_id');

  // Stripe cancellation
  if (reservationId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <FiXCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mt-6">Paiement annulé</h2>
            <p className="text-slate-600 mt-2">
              Votre paiement a été annulé. Aucun montant n'a été débité.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Votre réservation est toujours en attente. Vous pouvez réessayer le paiement à tout moment.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <Button 
                onClick={() => navigate(-1)} 
                className="w-full"
              >
                Réessayer le paiement
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="w-full"
              >
                Retour à l'accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fall back to MyPOS cancel page
  return (
    <PaymentCancelledPage
      onRetry={() => {
        navigate(-1);
      }}
      onGoBack={() => navigate('/')}
    />
  );
};

export default { PaymentSuccess, PaymentCancel };
